import React, {
    useEffect,
    useState
} from 'react'

import {
    ComposedChart,
    Bar,
    Cell,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Customized
} from 'recharts'

import { useQuery } from 'react-query'

import {
    useForm,
    Controller
} from 'react-hook-form'

import api from '../../../components/api'
import { exportarDashboardPDF } from '../../../components/exportarDashboardPDF'


export default function DashboardDistribuicao(props) {

    const {
        adesao_institucional_id,
        nomeHospital = '',
        anos = [],
        modo: modoDashboard = 'RELATORIO'
    } = props


    const anosSelecionados =
        Array.isArray(anos)
            ? anos
                .map(item =>
                    Number(item)
                )
                .filter(item =>
                    Number.isInteger(item) &&
                    item > 0
                )
            : []


    const anoSelecionado =
        anosSelecionados.length === 1
            ? anosSelecionados[0]
            : null


    // =========================================================
    // FORM
    // =========================================================

    const {
        control,
        watch,
        setValue
    } = useForm({

        defaultValues: {

            grupoResiduo_id: '',

            subGrupoResiduo_id: '',

            tipoTratamento_id: '',

            indicador: 'kg'

        }

    })


    // =========================================================
    // FILTROS
    // =========================================================

    const grupoResiduo_id =
        watch('grupoResiduo_id')


    const subGrupoResiduo_id =
        watch('subGrupoResiduo_id')


    const tipoTratamento_id =
        watch('tipoTratamento_id')

    const indicador =
        watch('indicador')


    const [binWidth, setBinWidth] =
        useState(null)

    const [gerandoPDF, setGerandoPDF] = useState(false)

        
    useEffect(() => {

        setBinWidth(null)

    }, [
        anosSelecionados.join(','),
        modoDashboard
    ])

    

    // =========================================================
    // TOOLTIP BOX-PLOT
    // =========================================================

    const [
        showBoxTooltip,
        setShowBoxTooltip
    ] = useState(false)


    // =========================================================
    // GRUPOS
    // =========================================================

    const {

        data: grupos = [],

        isLoading: loadingGrupos

    } = useQuery({

        queryKey: [
            'grupoResidDistribuicao'
        ],

        queryFn: async () => {

            const result =
                await api.get(
                    'grupoResid'
                )

            return result.data ?? []

        }

    })


    // =========================================================
    // SUBGRUPOS
    // =========================================================

    const {

        data: subgrupos = [],

        isLoading: loadingSubgrupos

    } = useQuery({

        queryKey: [
            'subGrupoResidDistribuicao',
            grupoResiduo_id
        ],

        queryFn: async () => {

            const result =
                await api.get(
                    `subGrupoResid/${grupoResiduo_id}`
                )

            return result.data ?? []

        },

        enabled:
            !!grupoResiduo_id

    })


    // =========================================================
    // TRATAMENTOS
    // =========================================================

    const {
        data: tratamentos = [],
        isLoading: loadingTratamentos
    } = useQuery(
        'tipoTratamentoTodosDashboard',
        async () => {

            const result =
                await api.get(
                    'tipoTratamentoTodos'
                )

            return result.data || []

        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000
        }
    )


    // =========================================================
    // DASHBOARD
    // =========================================================

    const {

        data,

        isLoading,

        isFetching,

        isError

    } = useQuery(

        [
            'residuosDashboardDistribuicao',
            adesao_institucional_id,
            anosSelecionados.join(','),
            modoDashboard,
            grupoResiduo_id,
            subGrupoResiduo_id,
            tipoTratamento_id,
            indicador,
            binWidth
        ],

        async () => {


            const result =
                await api.post(
                    'residuosDashboardDistribuicao',
                    {
                        adesao_institucional_id:
                            Number(
                                adesao_institucional_id
                            ),

                        anos:
                            anosSelecionados,

                        modo:
                            modoDashboard,

                        grupoResiduo_id:
                            grupoResiduo_id
                                ? Number(
                                    grupoResiduo_id
                                )
                                : null,

                        subgrupoResiduo_id:
                            subGrupoResiduo_id
                                ? Number(
                                    subGrupoResiduo_id
                                )
                                : null,

                        tipoTratamento_id:
                            tipoTratamento_id
                                ? Number(
                                    tipoTratamento_id
                                )
                                : null,

                        indicador:
                            indicador || 'kg',

                        binWidth:
                            binWidth !== null
                                ? Number(binWidth)
                                : null
                    }
                )


            return result.data

        },

        {
            enabled:
                !!adesao_institucional_id,

            refetchOnWindowFocus:
                false,

            keepPreviousData:
                true
        }

    )


    


    // =========================================================
    // ALTERA GRUPO
    // =========================================================

    const alteraGrupo = (
        field,
        valor
    ) => {

        field.onChange(valor)

        setValue(
            'subGrupoResiduo_id',
            ''
        )

    }


    // =========================================================
    // ALTERA SUBGRUPO
    // =========================================================

    const alteraSubgrupo = (
        field,
        valor
    ) => {

        field.onChange(valor)

    }


    // =========================================================
    // FORMATA NÚMERO
    // =========================================================

    const formataNumero = (
        valor,
        casas = 2
    ) => {

        if (
            valor === null ||
            valor === undefined
        ) {

            return '—'

        }


        return Number(
            valor
        ).toLocaleString(
            'pt-BR',
            {
                minimumFractionDigits:
                    casas,

                maximumFractionDigits:
                    casas
            }
        )

    }


    const unidade =
        data?.unidade || 'kg'

    const descricaoIndicador =
        data?.descricaoIndicador || 'Quilograma'

    const indicadorSelecionado =
        data?.indicador || indicador || 'kg'

    const tituloIndicador =
        indicadorSelecionado === 'kg'
            ? 'geração de resíduos'
            : descricaoIndicador

    // =========================================================
    // BOX-PLOT
    // =========================================================

    const BoxPlot = () => {

        const box =
            data?.boxplot


        if (!box) {

            return null

        }


        const valores = [

            box.minimo,

            box.q1,

            box.mediana,

            box.q3,

            box.maximo,

            ...(box.outliers || [])

        ]
            .map(Number)
            .filter(Number.isFinite)


        if (
            valores.length === 0
        ) {

            return null

        }


        // =====================================================
        // DIMENSÕES
        // =====================================================

        const largura =
            260


        const altura =
            360


        const topo =
            25


        const base =
            330


        const centroX =
            largura / 2


        const larguraBox =
            90


        const minEscala =
            Math.min(
                ...valores
            )


        const maxEscala =
            Math.max(
                ...valores
            )


        const range =
            maxEscala -
            minEscala || 1


        // =====================================================
        // CONVERTE VALOR PARA POSIÇÃO Y
        // =====================================================

        const y = valor => {

            return (

                base -

                (
                    (
                        Number(valor) -
                        minEscala
                    ) /
                    range
                ) *

                (
                    base -
                    topo
                )

            )

        }


        const yMin =
            y(box.minimo)


        const yQ1 =
            y(box.q1)


        const yMediana =
            y(box.mediana)


        const yQ3 =
            y(box.q3)


        const yMax =
            y(box.maximo)





        return (

            <div
                style={{
                    position:
                        'relative',

                    height:
                        '100%',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center'
                }}
            >

                <svg
                    width={
                        largura
                    }

                    height={
                        altura
                    }

                    viewBox={
                        `0 0 ${largura} ${altura}`
                    }

                    style={{
                        overflow:
                            'visible',

                        cursor:
                            'pointer'
                    }}

                    onMouseEnter={
                        () =>
                            setShowBoxTooltip(
                                true
                            )
                    }

                    onMouseLeave={
                        () =>
                            setShowBoxTooltip(
                                false
                            )
                    }
                >

                    {/* =========================================
                        LINHA CENTRAL
                       ========================================= */}

                    <line

                        x1={
                            centroX
                        }

                        x2={
                            centroX
                        }

                        y1={
                            yMax
                        }

                        y2={
                            yMin
                        }

                        stroke="#1683c7"

                        strokeWidth="2"

                    />


                    {/* =========================================
                        WHISKER SUPERIOR
                       ========================================= */}

                    <line

                        x1={
                            centroX - 25
                        }

                        x2={
                            centroX + 25
                        }

                        y1={
                            yMax
                        }

                        y2={
                            yMax
                        }

                        stroke="#1683c7"

                        strokeWidth="2"

                    />


                    {/* =========================================
                        WHISKER INFERIOR
                       ========================================= */}

                    <line

                        x1={
                            centroX - 25
                        }

                        x2={
                            centroX + 25
                        }

                        y1={
                            yMin
                        }

                        y2={
                            yMin
                        }

                        stroke="#1683c7"

                        strokeWidth="2"

                    />


                    {/* =========================================
                        CAIXA Q1 → Q3
                       ========================================= */}

                    <rect

                        x={
                            centroX -
                            (
                                larguraBox /
                                2
                            )
                        }

                        y={
                            yQ3
                        }

                        width={
                            larguraBox
                        }

                        height={
                            Math.max(
                                2,
                                yQ1 -
                                yQ3
                            )
                        }

                        rx="14"

                        fill="#9ccff2"

                        fillOpacity="0.85"

                        stroke="#1683c7"

                        strokeWidth="2"

                    />


                    {/* =========================================
                        MEDIANA
                       ========================================= */}

                    <line

                        x1={
                            centroX -
                            (
                                larguraBox /
                                2
                            )
                        }

                        x2={
                            centroX +
                            (
                                larguraBox /
                                2
                            )
                        }

                        y1={
                            yMediana
                        }

                        y2={
                            yMediana
                        }

                        stroke="#0f6fae"

                        strokeWidth="3"

                    />


                    {/* =========================================
                        OUTLIERS
                       ========================================= */}

                    {(box.outliers || [])
                        .map(
                            (
                                valor,
                                index
                            ) => (

                                <circle

                                    key={
                                        `${valor}-${index}`
                                    }

                                    cx={
                                        centroX
                                    }

                                    cy={
                                        y(valor)
                                    }

                                    r="4"

                                    fill="#e74c3c"

                                    stroke="#ffffff"

                                    strokeWidth="1"

                                />

                            )
                        )
                    }

                </svg>


                {/* =============================================
                    TOOLTIP BOX-PLOT
                   ============================================= */}

                {showBoxTooltip && (

                    <div
                        style={{
                            position:
                                'absolute',

                            top:
                                '20px',

                            left:
                                '50%',

                            transform:
                                'translateX(-50%)',

                            backgroundColor:
                                '#ffffff',

                            border:
                                '1px solid #d7dce1',

                            borderRadius:
                                '8px',

                            boxShadow:
                                '0 3px 12px rgba(0,0,0,0.15)',

                            padding:
                                '12px 15px',

                            zIndex:
                                20,

                            minWidth:
                                '190px',

                            fontSize:
                                '12px'
                        }}
                    >

                        <div
                            style={{
                                fontWeight:
                                    '700',

                                marginBottom:
                                    '8px'
                            }}
                        >
                            Box-Plot
                        </div>


                        <div>
                            Máximo:{' '}

                            <strong>
                                {formataNumero(
                                    box.maximo
                                )} {unidade}
                            </strong>
                        </div>


                        <div>
                            Q3:{' '}

                            <strong>
                                {formataNumero(
                                    box.q3
                                )} {unidade}
                            </strong>
                        </div>


                        <div>
                            Mediana:{' '}

                            <strong>
                                {formataNumero(
                                    box.mediana
                                )} {unidade}
                            </strong>
                        </div>


                        <div>
                            Q1:{' '}

                            <strong>
                                {formataNumero(
                                    box.q1
                                )} {unidade}
                            </strong>
                        </div>


                        <div>
                            Mínimo:{' '}

                            <strong>
                                {formataNumero(
                                    box.minimo
                                )} {unidade}
                            </strong>
                        </div>


                        <div
                            style={{
                                marginTop:
                                    '6px'
                            }}
                        >
                            Outliers:{' '}

                            <strong>
                                {
                                    box.outliers
                                        ?.length || 0
                                }
                            </strong>
                        </div>

                    </div>

                )}

            </div>

        )

    }



    // =========================================================
    // HISTOGRAMA + KDE
    // =========================================================

    const histograma =
        data?.histograma ?? []


    const kde =
        data?.kde ?? []


    // =========================================================
    // LIMITES DO HISTOGRAMA
    // =========================================================

    const minimoHistograma =
        histograma.length > 0
            ? Math.min(
                ...histograma.map(
                    item =>
                        Number(item.inicio)
                )
            )
            : 0


    const maximoHistograma =
        histograma.length > 0
            ? Math.max(
                ...histograma.map(
                    item =>
                        Number(item.fim)
                )
            )
            : 0


    // =========================================================
    // DADOS DAS BARRAS
    //
    // Cada barra fica posicionada pelo centro da classe.
    // =========================================================

    const dadosHistograma =
        histograma.map(item => ({

            x:
                Number(
                    item.centro
                ),

            inicio:
                Number(
                    item.inicio
                ),

            fim:
                Number(
                    item.fim
                ),

            quantidade:
                Number(
                    item.quantidade || 0
                ),

            quantidadePendencias:
                Number(
                    item.quantidadePendencias || 0
                ),

            possuiPendencia:
                modoDashboard === 'CORRECAO' &&
                item.possuiPendencia === true

        }))


// =========================================================
// DADOS DA KDE
//
// Aqui mantemos TODOS os pontos enviados pelo backend.
// Ex.: 80 pontos.
// =========================================================
const dadosKDE =
    kde.map(item => ({

        x:
            Number(
                item.x
            ),

        kde:
            Number(
                item.frequencia || 0
            )

    }))


// =========================================================
// CURVA KDE
// =========================================================

const CurvaKDE = props => {

    const {
        xAxisMap,
        yAxisMap
    } = props


    if (
        !dadosKDE ||
        dadosKDE.length < 2 ||
        !xAxisMap ||
        !yAxisMap
    ) {

        return null

    }


    const xAxis =
        Object.values(
            xAxisMap
        )[0]


    const yAxis =
        Object.values(
            yAxisMap
        )[0]


    if (
        !xAxis ||
        !yAxis ||
        !xAxis.scale ||
        !yAxis.scale
    ) {

        return null

    }


    const pontos =
        dadosKDE
            .map(item => {

                const posX =
                    xAxis.scale(
                        item.x
                    )


                const posY =
                    yAxis.scale(
                        item.kde
                    )


                if (
                    !Number.isFinite(posX) ||
                    !Number.isFinite(posY)
                ) {

                    return null

                }


                return {
                    x: posX,
                    y: posY
                }

            })
            .filter(Boolean)


    if (
        pontos.length < 2
    ) {

        return null

    }


    // =====================================================
    // MONTA PATH SUAVE
    // =====================================================

    let path =
        `M ${pontos[0].x} ${pontos[0].y}`


    for (
        let i = 1;
        i < pontos.length - 1;
        i++
    ) {

        const atual =
            pontos[i]

        const proximo =
            pontos[i + 1]


        const meioX =
            (
                atual.x +
                proximo.x
            ) / 2


        const meioY =
            (
                atual.y +
                proximo.y
            ) / 2


        path +=
            ` Q ${atual.x} ${atual.y} ${meioX} ${meioY}`

    }


    const ultimo =
        pontos[
            pontos.length - 1
        ]


    path +=
        ` T ${ultimo.x} ${ultimo.y}`


    return (

        <path
            d={path}
            fill="none"
            stroke="#e53935"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            pointerEvents="none"
        />

    )

}

    // =========================================================
    // TOOLTIP HISTOGRAMA
    // =========================================================

    const TooltipHistograma = ({
        active,
        payload
    }) => {

        if (
            !active ||
            !payload ||
            !payload.length
        ) {

            return null

        }


        const item =
            payload[0]?.payload


        if (!item) {

            return null

        }


        return (

            <div
                style={{
                    backgroundColor:
                        '#ffffff',

                    border:
                        '1px solid #d7dce1',

                    borderRadius:
                        '8px',

                    padding:
                        '12px 14px',

                    boxShadow:
                        '0 3px 12px rgba(0,0,0,0.12)',

                    minWidth:
                        '190px'
                }}
            >

                <div
                    style={{
                        fontWeight:
                            '700',

                        marginBottom:
                            '7px'
                    }}
                >

                    {formataNumero(
                        item.inicio
                    )} a{' '}

                    {formataNumero(
                        item.fim
                    )} {unidade}

                </div>


                <div
                    style={{
                        fontSize:
                            '13px'
                    }}
                >

                    Frequência:{' '}

                    <strong>
                        {
                            item.quantidade
                        }
                    </strong>

                </div>


                {item.possuiPendencia && (

                    <div
                        style={{
                            fontSize:
                                '12px',

                            marginTop:
                                '6px',

                            color:
                                '#e67e22',

                            fontWeight:
                                '600'
                        }}
                    >

                        <i
                            className="fa fa-exclamation-triangle"
                            style={{
                                marginRight:
                                    '5px'
                            }}
                        />

                        {
                            item.quantidadePendencias
                        } observação(ões) com pendência

                    </div>

                )}


                <div
                    style={{
                        fontSize:
                            '13px',

                        marginTop:
                            '3px'
                    }}
                >

                    KDE:{' '}

                    <strong>
                        {formataNumero(
                            item.kde
                        )}
                    </strong>

                </div>

            </div>

        )

    }


    // =========================================================
    // LOADING
    // =========================================================

    if (isLoading) {

        return (

            <div
                className="text-center"
                style={{
                    padding: '80px'
                }}
            >

                <i
                    className="fa fa-spinner fa-spin"
                    style={{
                        fontSize: '30px'
                    }}
                />

                <div
                    style={{
                        marginTop: '10px'
                    }}
                >
                    Carregando análise estatística...
                </div>

            </div>

        )

    }


    // =========================================================
    // ERRO
    // =========================================================

    if (isError) {

        return (

            <div className="alert alert-danger">

                Não foi possível carregar a análise estatística.

            </div>

        )

    }

    const chaveHistograma =
        `${binWidth}-${histograma.length}-${minimoHistograma}-${maximoHistograma}`


    const amplitudeHistograma =
    Number(
        data?.histogramaConfig?.amplitude || 0
    )


    const binWidthAutomatico =
        Number(
            data?.histogramaConfig?.binWidthAutomatico || 0
        )


    const binWidthAtual =
        Number(
            data?.histogramaConfig?.binWidth || 0
        )


    const binWidthMinimo =
        amplitudeHistograma > 0
            ? amplitudeHistograma / 100
            : 1


    const binWidthMaximo =
        amplitudeHistograma > 0
            ? amplitudeHistograma / 5
            : 20


    const binWidthSlider =
        binWidth !== null
            ? Number(binWidth)
            : binWidthAtual


    const passoBinWidth =
        Math.max(
            binWidthMinimo / 10,
            0.01
        )

    const handleExportarPDF = async () => {
        if (gerandoPDF || isFetching || data?.suficiente !== true) return

        try {
            setGerandoPDF(true)
            setShowBoxTooltip(false)

            await new Promise(resolve =>
                requestAnimationFrame(() => requestAnimationFrame(resolve))
            )

            await exportarDashboardPDF({
                elementoId: 'dashboard-distribuicao',
                tabelaId: 'tabela-dashboard-residuos',
                titulo: indicadorSelecionado === 'kg'
                    ? 'Distribuição da geração de resíduos'
                    : `Distribuição - ${descricaoIndicador}`,
                nomeHospital,
                nomeArquivo: `dashboard-distribuicao-${anosSelecionados.join('-') || 'todos'}.pdf`,
                logoUrl: `${process.env.PUBLIC_URL}/assets/pics/logo_HS.png`,
                anos: anosSelecionados
            })

        } catch (error) {
            console.error('Erro ao exportar dashboard:', error)
            window.alert('Não foi possível gerar o PDF. Tente novamente.')
        } finally {
            setGerandoPDF(false)
        }
    }

    return (

        <div>


            {/* =====================================================
                FILTROS
               ===================================================== */}

            <div
                style={{
                    backgroundColor:
                        '#f8f9fa',

                    border:
                        '1px solid #e1e5e8',

                    borderRadius:
                        '12px',

                    padding:
                        '18px',

                    marginBottom:
                        '20px'
                }}
            >

                <div className="row">


                    {/* =============================================
                        GRUPO
                       ============================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Grupo de resíduo
                            </label>


                            <Controller

                                name="grupoResiduo_id"

                                control={control}

                                render={({ field }) => (

                                    <select

                                        {...field}

                                        className="form-control"

                                        disabled={
                                            loadingGrupos
                                        }

                                        onChange={
                                            e =>
                                                alteraGrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >

                                        <option value="">
                                            Todos
                                        </option>


                                        {grupos.map(
                                            rs => (

                                                <option
                                                    key={
                                                        rs.grupoResiduo_id
                                                    }
                                                    value={
                                                        rs.grupoResiduo_id
                                                    }
                                                >

                                                    {
                                                        rs.grupoResiduo_descri
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>

                                )}

                            />

                        </div>

                    </div>


                    {/* =============================================
                        SUBGRUPO
                       ============================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Subgrupo
                            </label>


                            <Controller

                                name="subGrupoResiduo_id"

                                control={control}

                                render={({ field }) => (

                                    <select

                                        {...field}

                                        className="form-control"

                                        disabled={
                                            !grupoResiduo_id ||
                                            loadingSubgrupos
                                        }

                                        onChange={
                                            e =>
                                                alteraSubgrupo(
                                                    field,
                                                    e.target.value
                                                )
                                        }
                                    >

                                        <option value="">

                                            {
                                                !grupoResiduo_id
                                                    ? 'Selecione o grupo'
                                                    : 'Todos'
                                            }

                                        </option>


                                        {subgrupos.map(
                                            rs => (

                                                <option
                                                    key={
                                                        rs.subGrupoResiduo_id
                                                    }
                                                    value={
                                                        rs.subGrupoResiduo_id
                                                    }
                                                >

                                                    {
                                                        rs.subGrupoResiduo_descri
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>

                                )}

                            />

                        </div>

                    </div>


                    {/* =============================================
                        TRATAMENTO
                       ============================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Tratamento/Destinação
                            </label>


                            <Controller

                                name="tipoTratamento_id"

                                control={control}

                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                        disabled={
                                            loadingTratamentos
                                        }
                                    >

                                        <option value="">
                                            Todos
                                        </option>

                                        {tratamentos.map(rs => (

                                            <option
                                                key={
                                                    rs.tipoTratamento_id
                                                }
                                                value={
                                                    rs.tipoTratamento_id
                                                }
                                            >
                                                {
                                                    rs.tipoTratamento_descri
                                                }
                                            </option>

                                        ))}

                                    </select>

                                )}
                            />

                        </div>

                    </div>


                    {/* =============================================
                        INDICADOR
                       ============================================= */}

                    <div className="col-md-3">

                        <div className="form-group">

                            <label>
                                Indicador
                            </label>


                            <Controller

                                name="indicador"

                                control={control}

                                render={({ field }) => (

                                    <select
                                        {...field}
                                        className="form-control"
                                    >

                                         <option value="kg">
                                            Quilograma (kg)
                                        </option>

                                        <option value="kg_fte">
                                            kg/FTE
                                        </option>

                                        <option value="kg_paciente_dia">
                                            kg/Paciente Dia
                                        </option>

                                        <option value="kg_area">
                                            kg/Área construída
                                        </option>

                                    </select>

                                )}

                            />

                        </div>

                    </div>


                </div>

            </div>


            <div className="d-flex justify-content-end" style={{ marginBottom: '15px' }}>
                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleExportarPDF}
                    disabled={gerandoPDF || isFetching || data?.suficiente !== true}
                >
                    <i
                        className={`fa ${gerandoPDF ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'}`}
                        style={{ marginRight: '8px' }}
                    />
                    {gerandoPDF ? 'Gerando PDF...' : 'Exportar PDF'}
                </button>
            </div>

            <div id="dashboard-distribuicao" style={{ backgroundColor: '#fff', padding: '12px' }}>

            {/* =====================================================
                CABEÇALHO
               ===================================================== */}

            <div
                className="d-flex justify-content-between align-items-center"
                style={{
                    marginBottom:
                        '15px'
                }}
            >

                <div>

                    <div className="cabecalho-dashboard-interno">



                        <h4
                            style={{
                                margin: 0,
                                fontWeight: '600',
                                color: '#333'
                            }}
                        >
                            {indicadorSelecionado === 'kg'
                                ? 'Distribuição da geração de resíduos'
                                : `Distribuição - ${descricaoIndicador}`
                            }
                        </h4>

                        {nomeHospital && (
                            <div
                                style={{
                                    marginTop: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#24557F'
                                }}
                            >
                                <i
                                    className="fa fa-hospital-o"
                                    style={{ marginRight: '8px' }}
                                />

                                {nomeHospital}
                            </div>
                        )}


                        <div
                            style={{
                                fontSize: '13px',
                                color: '#777',
                                marginTop: '4px'
                            }}
                        >
                            Box-Plot e distribuição estatística de {tituloIndicador}

                            {anoSelecionado
                                ? ` em ${anoSelecionado}`
                                : anosSelecionados.length > 1
                                    ? ` nos ${anosSelecionados.length} anos selecionados`
                                    : ' em todos os anos'
                            }
                        </div>


                    </div>


                </div>


                <div
                    style={{
                        display:
                            'flex',

                        alignItems:
                            'center',

                        gap:
                            '15px'
                    }}
                >

                    {data?.n !== undefined && (

                        <span
                            style={{
                                backgroundColor:
                                    '#eef6fb',

                                border:
                                    '1px solid #c8e2f3',

                                borderRadius:
                                    '16px',

                                padding:
                                    '5px 12px',

                                fontSize:
                                    '12px',

                                color:
                                    '#555'
                            }}
                        >

                            n ={' '}

                            <strong>
                                {data.n}
                            </strong>

                        </span>

                    )}


                    {isFetching && (

                        <span
                            style={{
                                fontSize:
                                    '12px',

                                color:
                                    '#777'
                            }}
                        >

                            <i
                                className="fa fa-spinner fa-spin"
                                style={{
                                    marginRight:
                                        '5px'
                                }}
                            />

                            Atualizando...

                        </span>

                    )}

                </div>

            </div>


            {/* =====================================================
                DADOS INSUFICIENTES
               ===================================================== */}

            {data?.suficiente === false && (

                <div
                    style={{
                        minHeight:
                            '330px',

                        border:
                            '1px solid #e1e5e8',

                        borderRadius:
                            '14px',

                        backgroundColor:
                            '#fafafa',

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        textAlign:
                            'center',

                        padding:
                            '40px'
                    }}
                >

                    <div>

                        <i
                            className="fa fa-bar-chart"
                            style={{
                                fontSize:
                                    '42px',

                                color:
                                    '#b6c2ca',

                                marginBottom:
                                    '20px'
                            }}
                        />


                        <div
                            style={{
                                fontSize:
                                    '18px',

                                fontWeight:
                                    '600',

                                color:
                                    '#555',

                                maxWidth:
                                    '550px'
                            }}
                        >

                            {
                                data?.mensagem ||
                                'Sua organização ainda não tem uma quantidade de meses reportados suficientes para esta análise'
                            }

                        </div>


                        <div
                            style={{
                                color:
                                    '#888',

                                fontSize:
                                    '13px',

                                marginTop:
                                    '10px'
                            }}
                        >

                            Foram encontradas{' '}

                            <strong>
                                {data?.n || 0}
                            </strong>{' '}

                            observações. São necessárias mais de 30.

                        </div>

                    </div>

                </div>

            )}


            {/* =====================================================
                DASHBOARD
               ===================================================== */}

            {data?.suficiente === true && (

                <div className="row">


                    {/* =================================================
                        BOX-PLOT
                       ================================================= */}

                    <div className="col-md-3">

                        <div
                            style={{
                                height:
                                    '450px',

                                backgroundColor:
                                    '#ffffff',

                                border:
                                    '1px solid #d7dce1',

                                borderRadius:
                                    '14px',

                                padding:
                                    '15px',

                                boxShadow:
                                    '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >

                            <div
                                style={{
                                    textAlign:
                                        'center',

                                    fontSize:
                                        '15px',

                                    fontWeight:
                                        '600',

                                    color:
                                        '#444'
                                }}
                            >
                                {indicadorSelecionado === 'kg'
                                    ? 'Box-Plot da geração'
                                    : `Box-Plot - ${descricaoIndicador}`
                                }
                            </div>


                            <div
                                style={{
                                    height:
                                        '390px'
                                }}
                            >

                                <BoxPlot />

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        HISTOGRAMA + KDE
                       ================================================= */}

                    <div className="col-md-6">

                        <div
                            style={{
                                height:
                                    '450px',

                                backgroundColor:
                                    '#ffffff',

                                border:
                                    '1px solid #d7dce1',

                                borderRadius:
                                    '14px',

                                padding:
                                    '15px',

                                boxShadow:
                                    '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >

                            <div
                                style={{
                                    textAlign:
                                        'center',

                                    fontSize:
                                        '15px',

                                    fontWeight:
                                        '600',

                                    color:
                                        '#444',

                                    marginBottom:
                                        '5px'
                                }}
                            >
                                Histograma da distribuição
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginTop: '8px',
                                    marginBottom: '10px'
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Largura das classes:
                                </span>

                                <input
                                    type="range"
                                    min={binWidthMinimo}
                                    max={binWidthMaximo}
                                    step={passoBinWidth}
                                    value={binWidthSlider}
                                    onChange={e => {

                                        const valor =
                                            Number(
                                                e.target.value
                                            )

                                        setBinWidth(valor)

                                    }}
                                    style={{
                                        flex: 1
                                    }}
                                />

                                <strong
                                    style={{
                                        minWidth: '85px',
                                        textAlign: 'right'
                                    }}
                                >
                                    {formataNumero(
                                        binWidthSlider,
                                        2
                                    )}
                                </strong>
                                <span
                                    style={{
                                        fontSize: '12px',
                                        color: '#777',
                                        minWidth: '85px'
                                    }}
                                >
                                    {amplitudeHistograma > 0 &&
                                    binWidthSlider > 0
                                        ? `${Math.ceil(
                                            amplitudeHistograma /
                                            binWidthSlider
                                        )} classes`
                                        : ''
                                    }
                                </span>
                            </div>


                            <ResponsiveContainer
                                
                                width="100%"
                                height="90%"
                            >

                                <ComposedChart
                                    key={chaveHistograma}
                                    data={
                                        dadosHistograma
                                    }

                                    margin={{
                                        top:
                                            20,

                                        right:
                                            25,

                                        left:
                                            10,

                                        bottom:
                                            20
                                    }}
                                >

                                    <defs>

                                        <pattern
                                            id="distribuicaoPendencia"
                                            width="8"
                                            height="8"
                                            patternUnits="userSpaceOnUse"
                                            patternTransform="rotate(45)"
                                        >

                                            <rect
                                                width="8"
                                                height="8"
                                                fill="#fff4e8"
                                            />

                                            <line
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="8"
                                                stroke="#e67e22"
                                                strokeWidth="3"
                                            />

                                        </pattern>

                                    </defs>

                                    {/* =============================================
                                        GRADE
                                    ============================================= */}

                                    <CartesianGrid

                                        strokeDasharray="3 3"

                                        vertical={
                                            false
                                        }

                                    />


                                    {/* =============================================
                                        EIXO X
                                    ============================================= */}

                                    <XAxis

                                        dataKey="x"

                                        type="number"

                                        domain={[
                                            minimoHistograma,
                                            maximoHistograma
                                        ]}

                                        allowDataOverflow={
                                            false
                                        }

                                        tickFormatter={
                                            value =>
                                                Number(
                                                    value
                                                ).toLocaleString(
                                                    'pt-BR',
                                                    {
                                                        maximumFractionDigits:
                                                            0
                                                    }
                                                )
                                        }

                                    />


                                    {/* =============================================
                                        EIXO Y
                                    ============================================= */}

                                    <YAxis

                                        type="number"

                                        domain={[
                                            0,
                                            'auto'
                                        ]}

                                        allowDecimals={
                                            false
                                        }

                                    />


                                    {/* =============================================
                                        TOOLTIP
                                    ============================================= */}

                                    <Tooltip

                                        content={
                                            <TooltipHistograma />
                                        }

                                    />


                                    {/* =============================================
                                        HISTOGRAMA
                                    ============================================= */}

                                    <Bar

                                        dataKey="quantidade"

                                        name="Frequência"

                                        fill="#8bc5eb"

                                        stroke="#1683c7"

                                        barSize={
                                            30
                                        }

                                        radius={[
                                            4,
                                            4,
                                            0,
                                            0
                                        ]}
                                    >

                                        {dadosHistograma.map(
                                            (item, index) => (

                                                <Cell

                                                    key={
                                                        `classe-${index}`
                                                    }

                                                    fill={
                                                        item.possuiPendencia
                                                            ? 'url(#distribuicaoPendencia)'
                                                            : '#8bc5eb'
                                                    }

                                                    stroke={
                                                        item.possuiPendencia
                                                            ? '#e67e22'
                                                            : '#1683c7'
                                                    }

                                                />

                                            )
                                        )}

                                    </Bar>


                                    {/* =============================================
                                        KDE
                                    ============================================= */}

                                    <Customized
                                        component={
                                            CurvaKDE
                                        }
                                    />


                                </ComposedChart>

                            </ResponsiveContainer>

                        </div>

                    </div>


                    {/* =================================================
                        CARDS
                       ================================================= */}

                    <div className="col-md-3">


                        {/* =============================================
                            MÉDIA
                           ============================================= */}

                        <div
                            style={{
                                height:
                                    '215px',

                                border:
                                    '1px solid #1683c7',

                                borderRadius:
                                    '14px',

                                backgroundColor:
                                    '#eef8ff',

                                display:
                                    'flex',

                                flexDirection:
                                    'column',

                                justifyContent:
                                    'center',

                                alignItems:
                                    'center',

                                textAlign:
                                    'center',

                                padding:
                                    '20px',

                                marginBottom:
                                    '20px'
                            }}
                        >

                            <div
                                style={{
                                    color:
                                        '#1683c7',

                                    fontSize:
                                        '18px',

                                    fontWeight:
                                        '700',

                                    marginBottom:
                                        '18px'
                                }}
                            >
                                Média
                            </div>


                            <div
                                style={{
                                    fontSize:
                                        '34px',

                                    fontWeight:
                                        '700',

                                    color:
                                        '#222'
                                }}
                            >

                                {formataNumero(
                                    data
                                        ?.estatisticas
                                        ?.media
                                )}

                            </div>


                            <div
                                style={{
                                    fontSize:
                                        '13px',

                                    color:
                                        '#777',

                                    marginTop:
                                        '5px'
                                }}
                            >
                                {unidade}
                            </div>

                        </div>


                        {/* =============================================
                            DESVIO PADRÃO
                           ============================================= */}

                        <div
                            style={{
                                height:
                                    '215px',

                                border:
                                    '1px solid #1683c7',

                                borderRadius:
                                    '14px',

                                backgroundColor:
                                    '#eef8ff',

                                display:
                                    'flex',

                                flexDirection:
                                    'column',

                                justifyContent:
                                    'center',

                                alignItems:
                                    'center',

                                textAlign:
                                    'center',

                                padding:
                                    '20px'
                            }}
                        >

                            <div
                                style={{
                                    color:
                                        '#1683c7',

                                    fontSize:
                                        '18px',

                                    fontWeight:
                                        '700',

                                    marginBottom:
                                        '18px'
                                }}
                            >
                                Desvio padrão
                            </div>


                            <div
                                style={{
                                    fontSize:
                                        '34px',

                                    fontWeight:
                                        '700',

                                    color:
                                        '#222'
                                }}
                            >

                                {formataNumero(
                                    data
                                        ?.estatisticas
                                        ?.desvioPadrao
                                )}

                            </div>


                            <div
                                style={{
                                    fontSize:
                                        '13px',

                                    color:
                                        '#777',

                                    marginTop:
                                        '5px'
                                }}
                            >
                                {unidade}
                            </div>

                        </div>

                    </div>


                </div>

            )}

            </div>

        </div>

    )

}